import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import {
  FiActivity,
  FiAlertTriangle,
  FiBarChart2,
  FiBox,
  FiCheckCircle,
  FiChevronRight,
  FiClock,
  FiDollarSign,
  FiFileText,
  FiMapPin,
  FiNavigation,
  FiRefreshCw,
  FiSearch,
  FiTrendingUp,
  FiTruck,
  FiX,
} from "react-icons/fi";
import { dashboardApi } from "../services/dashboardApi";
import { trackingApi } from "../services/trackingApi";
import ShipsGoLiveMap from "../components/tracking/ShipsGoLiveMap";
import {
  buildTrackingSummary,
  getDestinationPort,
  getLastEvent,
  getOriginPort,
  getRouteProgress,
  getStatusClassName,
  getTrackingNo,
  getTrackingStatus,
  getVesselName,
  getVoyageNo,
} from "../utils/shipsGoTracking";

const AUTO_REFRESH_MS = 30 * 60 * 1000;

const emptyDashboard = {
  summary: {
    totalPayable: 0,
    finalBalance: 0,
    outstandingInvoices: 0,
    paidInvoices: 0,
    paidAmount: 0,
  },
  recentInvoices: [],
  meta: {
    updatedAt: null,
  },
};

const emptyTrackingSummary = {
  total: 0,
  inTransit: 0,
  arrived: 0,
  delivered: 0,
};

const formatMoney = (value) =>
  `£${Number(value || 0).toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value, withTime = false) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(withTime
      ? {
          hour: "2-digit",
          minute: "2-digit",
        }
      : {}),
  });
};

const extractTrackingRows = (response) => {
  const data = response?.data;

  if (Array.isArray(data?.shipments)) return data.shipments;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data)) return data;

  return [];
};

const invoiceStatusClass = (status) => {
  const value = String(status || "").toLowerCase();

  if (
    value.includes("paid") ||
    value.includes("completed") ||
    value.includes("delivered") ||
    value.includes("active")
  ) {
    return "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/70 dark:text-emerald-300";
  }

  if (
    value.includes("pending") ||
    value.includes("review") ||
    value.includes("new") ||
    value.includes("booked")
  ) {
    return "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/70 dark:bg-amber-950/70 dark:text-amber-300";
  }

  if (
    value.includes("overdue") ||
    value.includes("failed") ||
    value.includes("hold")
  ) {
    return "border-red-200 bg-red-50 text-red-700 dark:border-red-900/70 dark:bg-red-950/70 dark:text-red-300";
  }

  return "border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300";
};

function Card({ children, className = "" }) {
  return (
    <div
      className={`rounded-[28px] border border-slate-200/80 bg-white shadow-[0_14px_45px_rgba(15,23,42,0.06)] transition-all duration-300 dark:border-slate-800 dark:bg-slate-900/90 dark:shadow-[0_18px_55px_rgba(0,0,0,0.22)] ${className}`}
    >
      {children}
    </div>
  );
}

function StatusPill({ children, className = "" }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-bold capitalize ${className}`}
    >
      {children}
    </span>
  );
}

function SectionHeader({ title, subtitle, icon: Icon, action }) {
  return (
    <div className="flex flex-col gap-4 border-b border-slate-100 px-5 py-5 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between lg:px-6">
      <div className="flex min-w-0 items-start gap-3">
        {Icon ? (
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-blue-50 text-blue-600 dark:bg-blue-950/70 dark:text-blue-300">
            <Icon className="text-xl" />
          </div>
        ) : null}

        <div className="min-w-0">
          <h2 className="text-lg font-black tracking-tight text-slate-900 dark:text-white">
            {title}
          </h2>
          <p className="mt-1 text-sm leading-5 text-slate-500 dark:text-slate-400">
            {subtitle}
          </p>
        </div>
      </div>

      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  iconClassName,
  glowClassName,
  loading,
}) {
  if (loading) {
    return (
      <Card className="relative overflow-hidden p-5">
        <div className="animate-pulse">
          <div className="h-3 w-24 rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="mt-4 h-8 w-20 rounded-lg bg-slate-200 dark:bg-slate-800" />
          <div className="mt-3 h-3 w-40 rounded-full bg-slate-100 dark:bg-slate-800/70" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="group relative overflow-hidden p-5 hover:-translate-y-1 hover:border-blue-200 hover:shadow-[0_22px_55px_rgba(37,99,235,0.12)] dark:hover:border-blue-900/70">
      <div
        className={`pointer-events-none absolute -right-9 -top-10 h-28 w-28 rounded-full opacity-20 blur-3xl transition duration-500 group-hover:scale-125 group-hover:opacity-35 ${glowClassName}`}
      />

      <div className="relative flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[11px] font-black uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
            {title}
          </p>
          <p className="mt-3 text-3xl font-black tracking-tight text-slate-950 dark:text-white">
            {value}
          </p>
          <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
            {subtitle}
          </p>
        </div>

        <div
          className={`grid h-12 w-12 shrink-0 place-items-center rounded-2xl text-white shadow-lg transition duration-300 group-hover:rotate-3 group-hover:scale-105 ${iconClassName}`}
        >
          <Icon className="text-xl" />
        </div>
      </div>
    </Card>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-4">
      {[0, 1, 2].map((item) => (
        <div
          key={item}
          className="animate-pulse rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/60"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="h-4 w-32 rounded-full bg-slate-200 dark:bg-slate-800" />
            <div className="h-6 w-20 rounded-full bg-slate-200 dark:bg-slate-800" />
          </div>
          <div className="mt-4 h-3 w-56 rounded-full bg-slate-200 dark:bg-slate-800" />
          <div className="mt-4 h-2 rounded-full bg-slate-200 dark:bg-slate-800" />
        </div>
      ))}
    </div>
  );
}

function TrackingMiniCard({ shipment, active, onClick }) {
  const status = getTrackingStatus(shipment) || { label: "Unknown" };
  const progress = Math.min(100, Math.max(0, getRouteProgress(shipment) || 0));
  const lastEvent = getLastEvent(shipment);

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group w-full overflow-hidden rounded-2xl border p-4 text-left transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${
        active
          ? "border-blue-500/70 bg-gradient-to-br from-blue-50 to-cyan-50 shadow-[0_15px_35px_rgba(37,99,235,0.12)] dark:border-blue-700 dark:from-blue-950/70 dark:to-cyan-950/30"
          : "border-slate-200 bg-slate-50/80 hover:-translate-y-0.5 hover:border-blue-200 hover:bg-white hover:shadow-lg dark:border-slate-800 dark:bg-slate-950/60 dark:hover:border-slate-700 dark:hover:bg-slate-900"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span
              className={`h-2 w-2 shrink-0 rounded-full ${
                active ? "animate-pulse bg-blue-500" : "bg-slate-300 dark:bg-slate-700"
              }`}
            />
            <p className="truncate text-sm font-black text-slate-950 dark:text-white">
              {getTrackingNo(shipment)}
            </p>
          </div>

          <p className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">
            {shipment.shippingLine || "Shipping line unavailable"}
            {getVesselName(shipment) !== "-"
              ? ` • ${getVesselName(shipment)}`
              : ""}
          </p>
        </div>

        <StatusPill className={getStatusClassName(shipment)}>
          {status.label}
        </StatusPill>
      </div>

      <div className="mt-4 flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300">
        <span className="min-w-0 truncate">{getOriginPort(shipment)}</span>
        <FiNavigation className="shrink-0 text-blue-500" />
        <span className="min-w-0 truncate">{getDestinationPort(shipment)}</span>
      </div>

      <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
        <div
          className="h-full rounded-full bg-gradient-to-r from-blue-600 via-sky-500 to-cyan-400 transition-all duration-700 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="mt-2 flex items-center justify-between gap-3 text-[11px] text-slate-500 dark:text-slate-400">
        <span className="truncate">{lastEvent?.name || "Tracking event unavailable"}</span>
        <span className="shrink-0 font-black text-blue-600 dark:text-blue-300">
          {progress}%
        </span>
      </div>
    </button>
  );
}

function LiveTrackingMap({ shipment, refreshing, onRefresh }) {
  const status = getTrackingStatus(shipment) || { label: "Unknown" };
  const lastEvent = getLastEvent(shipment);

  return (
    <div className="overflow-hidden rounded-[26px] border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950">
      <div className="border-b border-slate-100 px-5 py-4 dark:border-slate-800">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
              </span>
              <p className="text-[11px] font-black uppercase tracking-[0.12em] text-emerald-600 dark:text-emerald-300">
                Live
              </p>
            </div>

            <h3 className="mt-2 truncate text-base font-black text-slate-950 dark:text-white">
              {getTrackingNo(shipment)}
            </h3>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {getOriginPort(shipment)} → {getDestinationPort(shipment)}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onRefresh}
              disabled={refreshing}
              className="inline-flex h-9 items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 text-xs font-bold text-blue-700 transition hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-blue-900/70 dark:bg-blue-950/70 dark:text-blue-300 dark:hover:bg-blue-900/70"
            >
              <FiRefreshCw className={refreshing ? "animate-spin" : ""} />
              Refresh
            </button>

            <StatusPill className={getStatusClassName(shipment)}>
              {status.label}
            </StatusPill>
          </div>
        </div>
      </div>

      <div className="relative p-3 sm:p-4">
        <ShipsGoLiveMap shipment={shipment} height={430} />
      </div>

      <div className="grid gap-3 border-t border-slate-100 p-4 dark:border-slate-800 sm:grid-cols-3">
        <MapMeta label="Vessel" value={getVesselName(shipment)} />
        <MapMeta label="Voyage" value={getVoyageNo(shipment)} />
        <MapMeta label="Latest event" value={lastEvent?.name || "-"} />
      </div>
    </div>
  );
}

function MapMeta({ label, value }) {
  return (
    <div className="rounded-2xl bg-slate-50 px-3 py-3 dark:bg-slate-900">
      <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400">
        {label}
      </p>
      <p className="mt-1 truncate text-xs font-bold text-slate-800 dark:text-slate-200">
        {value || "-"}
      </p>
    </div>
  );
}

function EmptyState({ icon: Icon, title, message }) {
  return (
    <div className="grid min-h-[260px] place-items-center rounded-3xl border border-dashed border-slate-300 bg-slate-50/70 px-6 text-center dark:border-slate-700 dark:bg-slate-950/50">
      <div>
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300">
          <Icon className="text-2xl" />
        </div>
        <h3 className="mt-4 font-black text-slate-900 dark:text-white">{title}</h3>
        <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-slate-500 dark:text-slate-400">
          {message}
        </p>
      </div>
    </div>
  );
}

function Notice({ notice, onClose }) {
  if (!notice) return null;

  const success = notice.type === "success";

  return (
    <div
      className={`fixed right-5 top-5 z-[100] flex max-w-sm items-start gap-3 rounded-2xl border px-4 py-3 shadow-2xl backdrop-blur-xl ${
        success
          ? "border-emerald-300/60 bg-emerald-50/95 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/95 dark:text-emerald-200"
          : "border-red-300/60 bg-red-50/95 text-red-800 dark:border-red-900 dark:bg-red-950/95 dark:text-red-200"
      }`}
    >
      {success ? (
        <FiCheckCircle className="mt-0.5 shrink-0 text-lg" />
      ) : (
        <FiAlertTriangle className="mt-0.5 shrink-0 text-lg" />
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-black">{notice.title}</p>
        <p className="mt-1 text-xs leading-5 opacity-80">{notice.message}</p>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="grid h-7 w-7 shrink-0 place-items-center rounded-lg transition hover:bg-black/5 dark:hover:bg-white/10"
        aria-label="Close notification"
      >
        <FiX />
      </button>
    </div>
  );
}

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboard, setDashboard] = useState(emptyDashboard);
  const [liveShipments, setLiveShipments] = useState([]);
  const [trackingSummary, setTrackingSummary] = useState(
    emptyTrackingSummary,
  );
  const [selectedTracking, setSelectedTracking] = useState(null);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [notice, setNotice] = useState(null);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");

  const mountedRef = useRef(true);
  const loadRunningRef = useRef(false);
  const noticeTimerRef = useRef(null);

  const showNotice = useCallback((nextNotice) => {
    setNotice(nextNotice);

    if (noticeTimerRef.current) {
      window.clearTimeout(noticeTimerRef.current);
    }

    noticeTimerRef.current = window.setTimeout(() => {
      setNotice(null);
    }, 4500);
  }, []);

  const applyTrackingRows = useCallback((rows) => {
    const safeRows = Array.isArray(rows) ? rows : [];
    const computedSummary = {
      ...emptyTrackingSummary,
      ...buildTrackingSummary(safeRows),
    };

    setLiveShipments(safeRows);
    setTrackingSummary(computedSummary);

    setSelectedTracking((previous) => {
      if (!safeRows.length) return null;

      const previousId = previous?.id;
      const existing = previousId
        ? safeRows.find((row) => row.id === previousId)
        : null;

      return existing || safeRows[0];
    });
  }, []);

  const loadDashboard = useCallback(
    async ({ silent = false, showFeedback = false } = {}) => {
      if (loadRunningRef.current) return;
      loadRunningRef.current = true;

      if (!silent) setLoading(true);

      try {
        const [dashboardResult, trackingResult] = await Promise.allSettled([
          dashboardApi.getOverview(),
          trackingApi.list(),
        ]);

        if (!mountedRef.current) return;

        let partialFailure = false;

        if (dashboardResult.status === "fulfilled") {
          const data = dashboardResult.value?.data || emptyDashboard;

          setDashboard({
            summary: {
              ...emptyDashboard.summary,
              ...(data.summary || {}),
            },
            recentInvoices: Array.isArray(data.recentInvoices)
              ? data.recentInvoices
              : [],
            meta: {
              ...emptyDashboard.meta,
              ...(data.meta || {}),
            },
          });
        } else {
          partialFailure = true;
          console.error("DASHBOARD OVERVIEW LOAD ERROR:", dashboardResult.reason);
        }

        if (trackingResult.status === "fulfilled") {
          applyTrackingRows(extractTrackingRows(trackingResult.value));
        } else {
          partialFailure = true;
          console.error("DASHBOARD TRACKING LOAD ERROR:", trackingResult.reason);
        }

        setLastSyncedAt(new Date());

        if (showFeedback) {
          showNotice(
            partialFailure
              ? {
                  type: "error",
                  title: "Dashboard partly refreshed",
                  message:
                    "Some information could not be refreshed. Existing data is still displayed.",
                }
              : {
                  type: "success",
                  title: "Dashboard refreshed",
                  message: "Tracking and financial information are up to date.",
                },
          );
        }
      } catch (error) {
        console.error("DASHBOARD LOAD ERROR:", error);

        if (showFeedback) {
          showNotice({
            type: "error",
            title: "Refresh failed",
            message: "The dashboard could not be refreshed. Please try again.",
          });
        }
      } finally {
        loadRunningRef.current = false;
        if (mountedRef.current && !silent) setLoading(false);
      }
    },
    [applyTrackingRows, showNotice],
  );

  const refreshSelectedTracking = useCallback(async () => {
    if (!selectedTracking?.id || refreshing) return;

    setRefreshing(true);

    try {
      const response = await trackingApi.refresh(selectedTracking.id);
      const updated =
        response?.data?.shipment || response?.data || selectedTracking;

      if (!mountedRef.current) return;

      setSelectedTracking(updated);
      setLiveShipments((previous) => {
        const next = previous.map((item) =>
          item.id === updated.id ? updated : item,
        );
        setTrackingSummary({
          ...emptyTrackingSummary,
          ...buildTrackingSummary(next),
        });
        return next;
      });
      setLastSyncedAt(new Date());

      showNotice({
        type: "success",
        title: "Tracking refreshed",
        message: `${getTrackingNo(updated)} was updated from ShipsGo.`,
      });
    } catch (error) {
      console.error("DASHBOARD TRACKING REFRESH ERROR:", error);
      showNotice({
        type: "error",
        title: "Tracking refresh failed",
        message: "ShipsGo data could not be refreshed for this shipment.",
      });
    } finally {
      if (mountedRef.current) setRefreshing(false);
    }
  }, [refreshing, selectedTracking, showNotice]);

  const handleRefreshAll = useCallback(async () => {
    if (refreshing) return;

    setRefreshing(true);

    try {
      await trackingApi.refreshAll();
      await loadDashboard({ silent: true, showFeedback: false });

      if (mountedRef.current) {
        showNotice({
          type: "success",
          title: "Everything refreshed",
          message: "Dashboard and ShipsGo tracking data are now up to date.",
        });
      }
    } catch (error) {
      console.error("DASHBOARD REFRESH ALL ERROR:", error);
      showNotice({
        type: "error",
        title: "Refresh failed",
        message: "The latest tracking information could not be loaded.",
      });
    } finally {
      if (mountedRef.current) setRefreshing(false);
    }
  }, [loadDashboard, refreshing, showNotice]);

  useEffect(() => {
    mountedRef.current = true;
    void loadDashboard({ silent: false });

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        void loadDashboard({ silent: true });
      }
    }, AUTO_REFRESH_MS);

    const handleFocus = () => {
      void loadDashboard({ silent: true });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void loadDashboard({ silent: true });
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      mountedRef.current = false;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);

      if (noticeTimerRef.current) {
        window.clearTimeout(noticeTimerRef.current);
      }
    };
  }, [loadDashboard]);

  const filteredLiveShipments = useMemo(() => {
    const query = search.trim().toLowerCase();

    return liveShipments
      .filter((item) => {
        const rawDate =
          item?.lastFetchedAt || item?.updatedAt || item?.createdAt;
        const itemDate = rawDate ? new Date(rawDate) : null;

        if (!itemDate || Number.isNaN(itemDate.getTime())) return true;

        if (fromDate) {
          const from = new Date(`${fromDate}T00:00:00`);
          if (itemDate < from) return false;
        }

        if (toDate) {
          const to = new Date(`${toDate}T23:59:59.999`);
          if (itemDate > to) return false;
        }

        return true;
      })
      .filter((item) => {
        if (!query) return true;

        const searchableText = [
          item?.requestId,
          item?.containerNumber,
          item?.blNumber,
          item?.bookingNumber,
          item?.shippingLine,
          item?.referenceNo,
          getTrackingStatus(item)?.label,
          getLastEvent(item)?.name,
          getVesselName(item),
          getVoyageNo(item),
          getOriginPort(item),
          getDestinationPort(item),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchableText.includes(query);
      })
      .slice(0, 5);
  }, [fromDate, liveShipments, search, toDate]);

  useEffect(() => {
    if (!filteredLiveShipments.length) {
      setSelectedTracking(null);
      return;
    }

    const selectedStillVisible = filteredLiveShipments.find(
      (item) => item.id === selectedTracking?.id,
    );

    if (!selectedStillVisible) {
      setSelectedTracking(filteredLiveShipments[0]);
    }
  }, [filteredLiveShipments, selectedTracking?.id]);

  const hasActiveFilters = Boolean(search.trim() || fromDate || toDate);

  const clearFilters = () => {
    setSearch("");
    setFromDate("");
    setToDate("");
  };

  const topStats = useMemo(
    () => [
      {
        title: "Total Tracking",
        value: trackingSummary.total || 0,
        subtitle: "All ShipsGo live records",
        icon: FiBox,
        iconClassName:
          "bg-gradient-to-br from-blue-600 to-indigo-600 shadow-blue-500/25",
        glowClassName: "bg-blue-500",
      },
      {
        title: "In Transit",
        value: trackingSummary.inTransit || 0,
        subtitle: "Booked, gate-in, loaded, or sailing",
        icon: FiTruck,
        iconClassName:
          "bg-gradient-to-br from-amber-500 to-orange-500 shadow-amber-500/25",
        glowClassName: "bg-amber-500",
      },
      {
        title: "Arrived at Port",
        value: trackingSummary.arrived || 0,
        subtitle: "Arrived, discharged, released, or gate-out",
        icon: FiMapPin,
        iconClassName:
          "bg-gradient-to-br from-violet-600 to-fuchsia-600 shadow-violet-500/25",
        glowClassName: "bg-violet-500",
      },
      {
        title: "Delivered",
        value: trackingSummary.delivered || 0,
        subtitle: "Delivered, completed, or empty returned",
        icon: FiCheckCircle,
        iconClassName:
          "bg-gradient-to-br from-emerald-500 to-teal-500 shadow-emerald-500/25",
        glowClassName: "bg-emerald-500",
      },
    ],
    [trackingSummary],
  );

  const financialStats = useMemo(
    () => [
      {
        title: "Total Payable",
        value: formatMoney(dashboard.summary.totalPayable),
        subtitle: `${dashboard.summary.outstandingInvoices || 0} outstanding invoices`,
        icon: FiAlertTriangle,
        iconClassName:
          "bg-gradient-to-br from-amber-500 to-orange-500 shadow-amber-500/25",
        glowClassName: "bg-amber-500",
      },
      {
        title: "Final Balance",
        value: formatMoney(dashboard.summary.finalBalance),
        subtitle: "Combined invoice balance",
        icon: FiDollarSign,
        iconClassName:
          "bg-gradient-to-br from-cyan-500 to-blue-600 shadow-cyan-500/25",
        glowClassName: "bg-cyan-500",
      },
      {
        title: "Outstanding",
        value: dashboard.summary.outstandingInvoices || 0,
        subtitle: "Invoices awaiting payment",
        icon: FiFileText,
        iconClassName:
          "bg-gradient-to-br from-orange-500 to-rose-500 shadow-orange-500/25",
        glowClassName: "bg-orange-500",
      },
      {
        title: "Paid Invoices",
        value: dashboard.summary.paidInvoices || 0,
        subtitle: formatMoney(dashboard.summary.paidAmount),
        icon: FiTrendingUp,
        iconClassName:
          "bg-gradient-to-br from-emerald-500 to-green-600 shadow-emerald-500/25",
        glowClassName: "bg-emerald-500",
      },
    ],
    [dashboard.summary],
  );

  const displayedUpdatedAt =
    lastSyncedAt ||
    (dashboard.meta.updatedAt ? new Date(dashboard.meta.updatedAt) : null);

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-50 px-4 py-5 text-slate-900 transition-colors duration-300 dark:bg-slate-950 dark:text-slate-100 sm:px-6 lg:px-8 lg:py-7">
      <Notice notice={notice} onClose={() => setNotice(null)} />

      <div className="pointer-events-none absolute -left-24 -top-32 h-80 w-80 rounded-full bg-blue-500/10 blur-3xl dark:bg-blue-500/10" />
      <div className="pointer-events-none absolute -right-20 top-36 h-72 w-72 rounded-full bg-violet-500/10 blur-3xl dark:bg-violet-500/10" />

      <div className="relative mx-auto max-w-[1800px] space-y-6">
        <header className="flex flex-col gap-5 2xl:flex-row 2xl:items-end 2xl:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
              </span>
              <span className="text-[11px] font-black uppercase tracking-[0.15em] text-emerald-600 dark:text-emerald-300">
                Live operations
              </span>
            </div>

            <h1 className="mt-2 text-3xl font-black tracking-[-0.04em] text-slate-950 dark:text-white sm:text-4xl">
              Dashboard Overview
            </h1>
    

            <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
              <span className="inline-flex items-center gap-1.5">
                <FiClock />
                {displayedUpdatedAt
                  ? `Updated ${formatDate(displayedUpdatedAt, true)}`
                  : "Waiting for first sync"}
              </span>
              <span className="hidden h-1 w-1 rounded-full bg-slate-300 dark:bg-slate-700 sm:block" />
              <span>Auto-refresh every 30 minutes</span>
            </div>
          </div>

          <div className="flex w-full flex-col gap-3 xl:w-auto xl:flex-row xl:flex-wrap xl:items-center xl:justify-end">
            <div className="relative min-w-0 xl:w-[280px]">
              <FiSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search container, BL, vessel..."
                className="h-12 w-full rounded-2xl border border-slate-200 bg-white pl-11 pr-10 text-sm font-medium text-slate-800 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100 dark:focus:border-blue-700"
              />
              {search ? (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-white"
                  aria-label="Clear search"
                >
                  <FiX />
                </button>
              ) : null}
            </div>

            <div className="flex min-w-0 items-center gap-2 rounded-2xl border border-slate-200 bg-white p-1.5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <input
                type="date"
                value={fromDate}
                onChange={(event) => setFromDate(event.target.value)}
                className="h-9 min-w-0 flex-1 rounded-xl bg-transparent px-2 text-xs font-semibold text-slate-700 outline-none dark:text-slate-200 dark:[color-scheme:dark] sm:w-[132px]"
                title="From date"
              />
              <span className="text-[10px] font-black uppercase text-slate-400">
                to
              </span>
              <input
                type="date"
                value={toDate}
                onChange={(event) => setToDate(event.target.value)}
                className="h-9 min-w-0 flex-1 rounded-xl bg-transparent px-2 text-xs font-semibold text-slate-700 outline-none dark:text-slate-200 dark:[color-scheme:dark] sm:w-[132px]"
                title="To date"
              />
              {(fromDate || toDate) && (
                <button
                  type="button"
                  onClick={() => {
                    setFromDate("");
                    setToDate("");
                  }}
                  className="grid h-8 w-8 shrink-0 place-items-center rounded-xl text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-white"
                  aria-label="Clear dates"
                >
                  <FiX />
                </button>
              )}
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleRefreshAll}
                disabled={loading || refreshing}
                className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-black text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:bg-slate-50 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 xl:flex-none"
              >
                <FiRefreshCw
                  className={loading || refreshing ? "animate-spin" : ""}
                />
                Refresh
              </button>

              <Link
                to="/reports"
                className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 px-5 text-sm font-black text-white shadow-[0_12px_30px_rgba(37,99,235,0.25)] transition hover:-translate-y-0.5 hover:from-blue-700 hover:to-indigo-700 xl:flex-none"
              >
                <FiBarChart2 />
                View Reports
              </Link>
            </div>
          </div>
        </header>

        {hasActiveFilters ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-blue-200 bg-blue-50/80 px-4 py-3 text-sm dark:border-blue-900/60 dark:bg-blue-950/30">
            <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200">
              <FiActivity />
              <span className="font-bold">
                Showing {filteredLiveShipments.length} of {liveShipments.length}
                {" "}tracking records
              </span>
            </div>
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-black text-blue-700 transition hover:bg-blue-100 dark:text-blue-300 dark:hover:bg-blue-900/50"
            >
              <FiX /> Clear filters
            </button>
          </div>
        ) : null}

        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 2xl:grid-cols-4">
          {topStats.map((item) => (
            <StatCard key={item.title} {...item} loading={loading} />
          ))}
        </section>

        <Card className="overflow-hidden">
          <SectionHeader
            title="Live Container Tracking"
            subtitle="Latest ShipsGo records with route progress, status, vessel, and exact map position."
            icon={FiNavigation}
            action={
              <Link
                to="/tracking"
                className="inline-flex h-10 items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3.5 text-xs font-black text-blue-700 transition hover:-translate-y-0.5 hover:bg-blue-100 dark:border-blue-900/70 dark:bg-blue-950/70 dark:text-blue-300 dark:hover:bg-blue-900/60"
              >
                <FiNavigation />
                Open Live Tracking
                <FiChevronRight />
              </Link>
            }
          />

          <div className="grid gap-5 p-4 lg:grid-cols-[minmax(300px,0.88fr)_minmax(0,1.35fr)] lg:p-6">
            <div className="min-w-0 space-y-3">
              <div className="flex items-center justify-between gap-3 px-1">
                <p className="text-xs font-black uppercase tracking-[0.12em] text-slate-400">
                  Recent shipments
                </p>
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-black text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                  {filteredLiveShipments.length} visible
                </span>
              </div>

              {loading ? (
                <DashboardSkeleton />
              ) : filteredLiveShipments.length === 0 ? (
                <EmptyState
                  icon={FiSearch}
                  title="No tracking records found"
                  message="Try clearing the search or date filters to display available ShipsGo records."
                />
              ) : (
                filteredLiveShipments.map((shipment) => (
                  <TrackingMiniCard
                    key={shipment.id}
                    shipment={shipment}
                    active={selectedTracking?.id === shipment.id}
                    onClick={() => setSelectedTracking(shipment)}
                  />
                ))
              )}
            </div>

            <div className="min-w-0">
              {selectedTracking ? (
                <LiveTrackingMap
                  shipment={selectedTracking}
                  refreshing={refreshing}
                  onRefresh={refreshSelectedTracking}
                />
              ) : (
                <EmptyState
                  icon={FiMapPin}
                  title="Select a tracking record"
                  message="Choose a shipment from the list to view its latest ShipsGo map position and route details."
                />
              )}
            </div>
          </div>
        </Card>

        <section>
          <div className="mb-4 flex items-end justify-between gap-4">
            <div>
              <h2 className="text-xl font-black tracking-tight text-slate-950 dark:text-white">
                Financial Overview
              </h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Current payable, balance, and invoice performance.
              </p>
            </div>

            <Link
              to="/invoices"
              className="hidden items-center gap-1.5 text-xs font-black text-blue-600 transition hover:text-blue-700 sm:inline-flex dark:text-blue-300"
            >
              Open invoices <FiChevronRight />
            </Link>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 2xl:grid-cols-4">
            {financialStats.map((item) => (
              <StatCard key={item.title} {...item} loading={loading} />
            ))}
          </div>
        </section>

        <Card className="overflow-hidden">
          <SectionHeader
            title="Recent Invoices"
            subtitle="Latest freight invoice activity and payment status."
            icon={FiFileText}
            action={
              <Link
                to="/invoices"
                className="inline-flex items-center gap-1.5 text-xs font-black text-blue-600 transition hover:text-blue-700 dark:text-blue-300"
              >
                View all invoices <FiChevronRight />
              </Link>
            }
          />

          <div className="overflow-x-auto">
            <div className="min-w-[760px]">
              <div className="grid grid-cols-[1.1fr_1fr_0.8fr_0.75fr_0.75fr] gap-4 border-b border-slate-100 bg-slate-50/80 px-6 py-3 text-[10px] font-black uppercase tracking-[0.12em] text-slate-400 dark:border-slate-800 dark:bg-slate-950/60">
                <span>Invoice</span>
                <span>Supplier</span>
                <span>Date</span>
                <span>Status</span>
                <span className="text-right">Total</span>
              </div>

              {loading ? (
                <div className="space-y-3 p-6">
                  {[0, 1, 2, 3].map((item) => (
                    <div
                      key={item}
                      className="grid animate-pulse grid-cols-[1.1fr_1fr_0.8fr_0.75fr_0.75fr] gap-4"
                    >
                      {[0, 1, 2, 3, 4].map((cell) => (
                        <div
                          key={cell}
                          className="h-4 rounded-full bg-slate-200 dark:bg-slate-800"
                        />
                      ))}
                    </div>
                  ))}
                </div>
              ) : dashboard.recentInvoices.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-slate-100 text-slate-400 dark:bg-slate-800">
                    <FiFileText className="text-2xl" />
                  </div>
                  <p className="mt-4 font-black text-slate-900 dark:text-white">
                    No invoices found
                  </p>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Recent invoice activity will appear here.
                  </p>
                </div>
              ) : (
                dashboard.recentInvoices.map((item) => (
                  <div
                    key={item.id}
                    className="grid grid-cols-[1.1fr_1fr_0.8fr_0.75fr_0.75fr] items-center gap-4 border-b border-slate-100 px-6 py-4 text-sm transition hover:bg-blue-50/50 last:border-b-0 dark:border-slate-800 dark:hover:bg-blue-950/20"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-black text-slate-900 dark:text-white">
                        {item.ref || "-"}
                      </p>
                      <p className="mt-1 text-[11px] text-slate-400">
                        Freight invoice
                      </p>
                    </div>

                    <p className="truncate font-semibold text-slate-600 dark:text-slate-300">
                      {item.supplier || "-"}
                    </p>

                    <p className="text-slate-500 dark:text-slate-400">
                      {formatDate(item.date)}
                    </p>

                    <div>
                      <StatusPill className={invoiceStatusClass(item.status)}>
                        {item.status || "Unknown"}
                      </StatusPill>
                    </div>

                    <p className="text-right font-black text-slate-900 dark:text-white">
                      {formatMoney(item.total)}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}